import { ElementType } from 'react';
import { PiWarningBold } from 'react-icons/pi';
import {
  CaseLogCaseIcon,
  CaseLogFeedbackIcon,
  CaseLogMoneyIcon,
  CaseLogRouteIcon,
  CaseLogUpdateIcon,
  CaseLogUsersIcon,
  HeartSmallIcon,
  HeartSmallUrl,
  KidneySmallIcon,
  KidneySmallUrl,
  LiverSmallIcon,
  LiverSmallUrl,
  LungsSmallIcon,
  LungsSmallUrl,
} from '@icons';
import { invert, partition, uniqBy } from 'lodash-es';
import { Location, Uuid } from '@/api/types';
import { HereGeocodeItemType } from '@/api/apis/here-maps';
import { CountryEntryView } from '@/api/apis/facilities';
import { OfferOrganType } from '@/api/apis/offers/offer.types';
import {
  Case,
  CaseLogGroup,
  CaseStatus,
  ClinicalData,
  ClinicalDataRecord,
  ClinicalItemType,
  Laterality,
  OrganType,
  OutcomeData,
  OutcomeType,
  Randomization,
  RandomizationResultType,
  ResourceLogisticsStatus,
  ServiceType,
  TrialAnswer,
  TrialAnsweredQuestionModel,
  TrialQuestion,
} from '@/api/apis/case';
import {
  caseLogisticsProviders,
  caseStatusNames,
  KIDNEY_LATERALITY_LETTER,
  logisticsStatusList,
  nextCaseStatus,
  organSuffixes,
  outcomeTypeNames,
  prevCaseStatus,
  routeLegToProviderTypeMap,
  statusesForServiceDecline,
  statusList,
  trialProgramByOrgan,
  turndownOutcomeNames,
} from '@/constants/case/case.constants';
import { AviaLegTypes, GroundLegTypes } from '@/api/apis/route';
import { PerfusionSetOrganType, ReservationData, ResourceStatus } from '@/api/apis/resources';
import { DataPresentation } from '@/constants/data-presentation.constans';
import { formatOutput } from '@/utils/helpers/string.helpers';
import { TrialProgram } from '@/api/apis/facilities/trial-programs';
import { getUrl } from '@/utils/helpers/url.helpers';

const getDefinedLaterality = (laterality?: Laterality | null) =>
  laterality === Laterality.Left || laterality === Laterality.Right ? laterality : undefined;

export const getCaseSuffixes = (
  organType: OrganType,
  reallocationNumber?: number,
  opoRequest?: boolean,
  isExpeditedCase?: boolean,
  laterality?: Laterality | null,
  duplicateCaseNumber?: number,
) => {
  const definedLaterality = getDefinedLaterality(laterality);
  const kidneySuffix =
    organType === OrganType.Kidney && (definedLaterality || duplicateCaseNumber)
      ? `/${definedLaterality ? KIDNEY_LATERALITY_LETTER[definedLaterality] : duplicateCaseNumber}`
      : '';
  const organSuffix = `${organSuffixes[organType] ?? ''}${kidneySuffix}`;
  const reallocationSuffix = reallocationNumber ? `R${reallocationNumber} ` : '';
  const opoRequestSuffix = opoRequest ? 'O ' : '';
  const expeditedSuffix = isExpeditedCase ? 'E' : '';
  return `${organSuffix} ${reallocationSuffix}${opoRequestSuffix}${expeditedSuffix}`.trim();
};

export const getUnosId = (
  id: string,
  organType: OrganType,
  reallocationNumber?: number,
  opoRequest?: boolean,
  isExpeditedCase?: boolean,
  laterality?: Laterality | null,
  duplicateCaseNumber?: number,
) => {
  const suffixes = getCaseSuffixes(
    organType,
    reallocationNumber,
    opoRequest,
    isExpeditedCase,
    laterality,
    duplicateCaseNumber,
  );

  return `${id} ${suffixes}`.trim();
};

export const getOrganTypeFromUnosId = (unosId: string) => {
  const organSuffix = unosId.match(/[A-Z0-9]+ ?(HT|LI|LU|KI)/)?.[1];
  const suffixToOrganMap = invert(organSuffixes) as Record<string, OrganType>;

  return organSuffix ? (suffixToOrganMap[organSuffix] ?? null) : null;
};

export const getCountryIso2 = (countryCode: string, countriesByIso2: Record<string, CountryEntryView>): string => {
  if (countriesByIso2[countryCode]) return countryCode;

  return Object.values(countriesByIso2).find((c) => c.alpha3 === countryCode)?.alpha2 ?? countryCode;
};

export const convertHereLocation = (
  data: HereGeocodeItemType,
  countriesByIso2: Record<string, CountryEntryView> = {},
): Omit<Location, 'region' | 'regionId'> => {
  const countryCode = getCountryIso2(data.address.countryCode, countriesByIso2);

  const addressLine = data.address.label.split(',')[0] || data.address.label;

  return {
    id: data.id,
    country: countryCode,
    addressLine,
    state: data.address.stateCode || data.address.state,
    city: data.address.city,
    postalCode: data.address.postalCode,
    latitude: data.position.lat,
    longitude: data.position.lng,
    timezone: data.timeZone.name,
  };
};

export const getLocationAddress = (
  location?: Pick<Location, 'country' | 'city' | 'postalCode' | 'addressLine'> & {
    state: string | null;
  },
  resolveSubdivisionName?: (stateCode?: string | null, countryCode?: string | null) => string | undefined,
): string => {
  if (!location || !location?.postalCode) return '';
  const { country, state, city, postalCode, addressLine } = location;
  const stateName = resolveSubdivisionName?.(state, country) ?? state;
  const stateStr = stateName ? `${stateName} ` : '';
  return `${addressLine}, ${city}, ${stateStr}${postalCode}, ${country}`;
};

// TODO: add a Kidney entry once a KidneySmallIcon asset exists in @icons
export const ORGAN_ICON: Record<OrganType, ElementType> = {
  [OrganType.Heart]: HeartSmallIcon as ElementType,
  [OrganType.Liver]: LiverSmallIcon as ElementType,
  [OrganType.Lungs]: LungsSmallIcon as ElementType,
  [OrganType.Kidney]: KidneySmallIcon as ElementType,
};

export const PERFUSION_SET_ORGAN_ICON: Record<PerfusionSetOrganType, ElementType> = {
  [PerfusionSetOrganType.HeartPerfusionSet]: HeartSmallIcon as ElementType,
  [PerfusionSetOrganType.LiverPerfusionSet]: LiverSmallIcon as ElementType,
  [PerfusionSetOrganType.LungPerfusionSet]: LungsSmallIcon as ElementType,
  [PerfusionSetOrganType.KidneyPerfusionSet]: KidneySmallIcon as ElementType,
};

export const CASE_LOG_GROUP_ICON: Record<CaseLogGroup, ElementType> = {
  [CaseLogGroup.CaseFeedback]: CaseLogFeedbackIcon as ElementType,
  [CaseLogGroup.CaseLogistics]: CaseLogRouteIcon as ElementType,
  [CaseLogGroup.CaseManagement]: CaseLogCaseIcon as ElementType,
  [CaseLogGroup.CaseUpdate]: CaseLogUpdateIcon as ElementType,
  [CaseLogGroup.FinancialManagement]: CaseLogMoneyIcon as ElementType,
  [CaseLogGroup.ResourceManagement]: CaseLogUsersIcon as ElementType,
  [CaseLogGroup.AchieveCaseAccess]: CaseLogUsersIcon as ElementType,
  [CaseLogGroup.Unknown]: PiWarningBold,
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  [undefined]: PiWarningBold,
};

export const formatTsaNumber = (tsaPreCheckNumber: string) => {
  const cleaned = tsaPreCheckNumber.replace(/[^a-zA-Z0-9]/g, '');

  if (cleaned.length === 9) {
    const match = cleaned.match(/^(.{3})(.{3})(.{3})$/);
    if (match) {
      return [match[1], ' ', match[2], ' ', match[3]].join('');
    }
  } else if (cleaned.length === 10) {
    const match = cleaned.match(/^(.{3})(.{4})(.{3})$/);
    if (match) {
      return [match[1], ' ', match[2], ' ', match[3]].join('');
    }
  }

  return null;
};

export const getColorsByOrganType = (organType?: OfferOrganType | PerfusionSetOrganType) => {
  switch (organType) {
    case OrganType.Heart:
    case PerfusionSetOrganType.HeartPerfusionSet:
      return {
        bgDefault: `bg-red`,
        bgSaturated: `bg-red-saturated`,
        bg40: `bg-red-40`,
        bg50: `bg-red-50`,
        bg55: `bg-red-55`,
        bg60: `bg-red-60`,
        bg70: `bg-red-70`,
        bg80: `bg-red-80`,
        bg90: `bg-red-90`,
        textDefault: `text-red`,
        textSaturated: `text-red-saturated`,
        text40: `text-red-40`,
        text50: `text-red-50`,
        text60: `text-red-60`,
        text70: `text-red-70`,
        text80: `text-red-80`,
        text90: `text-red-90`,
        borderDefault: `border-red`,
        borderSaturated: `border-red-saturated`,
        border50: `border-red-50`,
        border60: `border-red-60`,
        border70: `border-red-70`,
        border80: `border-red-80`,
        border90: `border-red-90`,
      };
    case OrganType.Liver:
    case PerfusionSetOrganType.LiverPerfusionSet:
      return {
        bgDefault: `bg-green`,
        bgSaturated: `bg-green-saturated`,
        bg40: `bg-green-40`,
        bg50: `bg-green-50`,
        bg55: `bg-green-55`,
        bg60: `bg-green-60`,
        bg70: `bg-green-70`,
        bg80: `bg-green-80`,
        bg90: `bg-green-90`,
        textDefault: `text-green`,
        textSaturated: `text-green-saturated`,
        text40: `text-green-40`,
        text50: `text-green-50`,
        text60: `text-green-60`,
        text70: `text-green-70`,
        text80: `text-green-80`,
        text90: `text-green-90`,
        borderDefault: `border-green`,
        borderSaturated: `border-green-saturated`,
        border50: `border-green-50`,
        border60: `border-green-60`,
        border70: `border-green-70`,
        border80: `border-green-80`,
        border90: `border-green-90`,
      };
    case OrganType.Lungs:
    case PerfusionSetOrganType.LungPerfusionSet:
      return {
        bgDefault: `bg-blue`,
        bgSaturated: `bg-blue-saturated`,
        bg40: `bg-blue-40`,
        bg50: `bg-blue-50`,
        bg55: `bg-blue-55`,
        bg60: `bg-blue-60`,
        bg70: `bg-blue-70`,
        bg80: `bg-blue-80`,
        bg90: `bg-blue-90`,
        textDefault: `text-blue`,
        textSaturated: `text-blue-saturated`,
        text40: `text-blue-40`,
        text50: `text-blue-50`,
        text60: `text-blue-60`,
        text70: `text-blue-70`,
        text80: `text-blue-80`,
        text90: `text-blue-90`,
        borderDefault: `border-blue`,
        borderSaturated: `border-blue-saturated`,
        border50: `border-blue-50`,
        border60: `border-blue-60`,
        border70: `border-blue-70`,
        border80: `border-blue-80`,
        border90: `border-blue-90`,
      };
    case OrganType.Kidney:
    case PerfusionSetOrganType.KidneyPerfusionSet:
      return {
        bgDefault: `bg-yellow`,
        bgSaturated: `bg-yellow-saturated`,
        bg40: `bg-yellow-40`,
        bg50: `bg-yellow-50`,
        bg55: `bg-yellow-55`,
        bg60: `bg-yellow-60`,
        bg70: `bg-yellow-70`,
        bg80: `bg-yellow-80`,
        bg90: `bg-yellow-90`,
        textDefault: `text-yellow`,
        textSaturated: `text-yellow-saturated`,
        text40: `text-yellow-40`,
        text50: `text-yellow-50`,
        text60: `text-yellow-60`,
        text70: `text-yellow-70`,
        text80: `text-yellow-80`,
        text90: `text-yellow-90`,
        borderDefault: `border-yellow`,
        borderSaturated: `border-yellow-saturated`,
        border50: `border-yellow-50`,
        border60: `border-yellow-60`,
        border70: `border-yellow-70`,
        border80: `border-yellow-80`,
        border90: `border-yellow-90`,
      };
    case OfferOrganType.Pancreas:
      return {
        bgDefault: `bg-pancreas`,
        bgSaturated: `bg-pancreas-saturated`,
        bg40: `bg-pancreas-40`,
        bg50: `bg-pancreas-50`,
        bg55: `bg-pancreas-55`,
        bg60: `bg-pancreas-60`,
        bg70: `bg-pancreas-70`,
        bg80: `bg-pancreas-80`,
        bg90: `bg-pancreas-90`,
        textDefault: `text-pancreas`,
        textSaturated: `text-pancreas-saturated`,
        text40: `text-pancreas-40`,
        text50: `text-pancreas-50`,
        text60: `text-pancreas-60`,
        text70: `text-pancreas-70`,
        text80: `text-pancreas-80`,
        text90: `text-pancreas-90`,
        borderDefault: `border-pancreas`,
        borderSaturated: `border-pancreas-saturated`,
        border50: `border-pancreas-50`,
        border60: `border-pancreas-60`,
        border70: `border-pancreas-70`,
        border80: `border-pancreas-80`,
        border90: `border-pancreas-90`,
      };
    case OfferOrganType.KidneyPancreas:
      return {
        bgDefault: `bg-kidney-pancreas`,
        bgSaturated: `bg-kidney-pancreas-saturated`,
        bg40: `bg-kidney-pancreas-40`,
        bg50: `bg-kidney-pancreas-50`,
        bg55: `bg-kidney-pancreas-55`,
        bg60: `bg-kidney-pancreas-60`,
        bg70: `bg-kidney-pancreas-70`,
        bg80: `bg-kidney-pancreas-80`,
        bg90: `bg-kidney-pancreas-90`,
        textDefault: `text-kidney-pancreas`,
        textSaturated: `text-kidney-pancreas-saturated`,
        text40: `text-kidney-pancreas-40`,
        text50: `text-kidney-pancreas-50`,
        text60: `text-kidney-pancreas-60`,
        text70: `text-kidney-pancreas-70`,
        text80: `text-kidney-pancreas-80`,
        text90: `text-kidney-pancreas-90`,
        borderDefault: `border-kidney-pancreas`,
        borderSaturated: `border-kidney-pancreas-saturated`,
        border50: `border-kidney-pancreas-50`,
        border60: `border-kidney-pancreas-60`,
        border70: `border-kidney-pancreas-70`,
        border80: `border-kidney-pancreas-80`,
        border90: `border-kidney-pancreas-90`,
      };
    default:
      return {
        bgDefault: `bg-base`,
        bgSaturated: `bg-base-saturated`,
        bg40: `bg-base-40`,
        bg50: `bg-base-50`,
        bg55: `bg-base-55`,
        bg60: `bg-base-60`,
        bg70: `bg-base-70`,
        bg80: `bg-base-80`,
        bg90: `bg-base-90`,
        textDefault: `text-base-20`,
        textSaturated: `text-base-saturated`,
        text40: `text-base-40`,
        text50: `text-base-50`,
        text60: `text-base-60`,
        text70: `text-base-70`,
        text80: `text-base-80`,
        text90: `text-base-90`,
        borderDefault: `border-base`,
        borderSaturated: `border-base-saturated`,
        border50: `border-base-50`,
        border60: `border-base-60`,
        border70: `border-base-70`,
        border80: `border-base-80`,
        border90: `border-base-90`,
      };
  }
};

export const getOrganImg = (organType?: OrganType | PerfusionSetOrganType) => {
  switch (organType) {
    case OrganType.Heart:
    case PerfusionSetOrganType.HeartPerfusionSet:
      return HeartSmallUrl;
    case OrganType.Liver:
    case PerfusionSetOrganType.LiverPerfusionSet:
      return LiverSmallUrl;
    case OrganType.Lungs:
    case PerfusionSetOrganType.LungPerfusionSet:
      return LungsSmallUrl;
    case OrganType.Kidney:
    case PerfusionSetOrganType.KidneyPerfusionSet:
      return KidneySmallUrl;
    default:
      return '';
  }
};

export const getColorByStatus = (status: CaseStatus) => {
  if ([CaseStatus.Accepted, CaseStatus.OrganOffOCS].includes(status)) {
    return 'bg-success-50! text-white!';
  }
  if ([CaseStatus.Declined, CaseStatus.ServiceDeclined].includes(status)) {
    return 'bg-danger-50! text-white!';
  }
  return 'bg-white! text-black!';
};

export const getColorByNotificationStatus = (status: CaseStatus) => {
  if ([CaseStatus.Accepted, CaseStatus.Finished, CaseStatus.OrganOffOCS].includes(status)) {
    return 'bg-success-50! text-white!';
  }
  if ([CaseStatus.Declined, CaseStatus.ServiceDeclined, CaseStatus.QuoteRejected].includes(status)) {
    return 'bg-danger-50! text-white!';
  }
  return '!bg-grey text-black!';
};

type FilterRecordByType<R, T> = R extends { clinicalItemType: infer CIT } ? (T extends CIT ? R : never) : never;

export type ClinicalRecordForType<T extends ClinicalItemType> = FilterRecordByType<ClinicalDataRecord, T>;

export const getClinicalRecordByType = <T extends ClinicalItemType>(
  clinicalData: ClinicalData,
  type: T,
): ClinicalRecordForType<T> | undefined =>
  clinicalData.find((r) => r.clinicalItemType === type) as ClinicalRecordForType<T> | undefined;

const isLateralityMissing = (laterality?: Laterality | null): boolean =>
  !laterality || laterality === Laterality.NotDefined;

export const getLateralityBlockReason = ({
  organType,
  laterality,
  nextStatus,
}: {
  organType?: OrganType;
  laterality?: Laterality | null;
  nextStatus?: CaseStatus;
}): ProceedBlockReason =>
  organType === OrganType.Kidney &&
  isLateralityMissing(laterality) &&
  (nextStatus === CaseStatus.Closed || nextStatus === CaseStatus.Finished)
    ? 'missingLaterality'
    : null;

export type ProceedBlockReason =
  | 'missingLaterality'
  | 'missingQuestionnaire'
  | 'missingRandomization'
  | 'missingOrganApproval'
  | null;

const proceedBlockReasonMessages: Record<Exclude<ProceedBlockReason, null>, string> = {
  missingLaterality: 'The organ laterality is not set.',
  missingQuestionnaire: 'The trial questionnaire is not completed',
  missingRandomization: 'Proceed is unavailable until randomization outcome is recorded',
  missingOrganApproval: 'Proceed is unavailable until organ approval is completed',
};

export const getProceedActionHintMessage = (proceedBlockReason: ProceedBlockReason): string =>
  proceedBlockReason ? proceedBlockReasonMessages[proceedBlockReason] : '';

export const getTargetProceedStatus = (
  serviceType: ServiceType | undefined,
  status: CaseStatus,
): CaseStatus | undefined =>
  serviceType === ServiceType.DirectAcquisition && status === CaseStatus.ClinicalScreening
    ? CaseStatus.Closed
    : undefined;

export const getCaseWasDeclined = (outcomeData: OutcomeData | null | undefined): boolean =>
  !!outcomeData && outcomeData.outcomeType !== OutcomeType.Transplant;

export const getActionsForStatus = (status: CaseStatus, caseWasDeclined: boolean) => {
  const nextStatus = nextCaseStatus[status as keyof typeof nextCaseStatus];
  let prevStatus = prevCaseStatus[status as keyof typeof prevCaseStatus];
  if (caseWasDeclined && status === CaseStatus.Finished) {
    prevStatus = CaseStatus.ServiceDeclined;
  }
  const canMoveToPrevStatus = !!prevStatus;
  const canMoveToNextStatus = !!nextStatus;
  const canDeclineCase = status === CaseStatus.ClinicalScreening;
  const canDeclineService = statusesForServiceDecline.includes(status);
  const canChangeStatus = canMoveToPrevStatus || canMoveToNextStatus || canDeclineCase || canDeclineService;

  return {
    nextStatus,
    prevStatus,
    canMoveToPrevStatus,
    canMoveToNextStatus,
    canDeclineCase,
    canDeclineService,
    canChangeStatus,
  };
};

type IsAllowedStatusArgs<T> = {
  statusFrom?: T;
  statusTo?: T;
  currentStatus: T;
  prohibitDeclined?: boolean;
};

export const isAllowedStatus = ({
  statusFrom = CaseStatus.Draft,
  statusTo = CaseStatus.Closed,
  currentStatus,
  prohibitDeclined = false,
}: IsAllowedStatusArgs<CaseStatus>) => {
  const firstAvailableIndex = statusList.indexOf(statusFrom);
  const lastAvailableIndex = statusList.indexOf(statusTo);
  const currentIndex = statusList.indexOf(currentStatus);
  const inAllowedRange = currentIndex >= firstAvailableIndex && currentIndex <= lastAvailableIndex;
  const isDeclined = prohibitDeclined && currentStatus === CaseStatus.Declined;
  return inAllowedRange && !isDeclined;
};

export const isLogisticsStatusInRange = ({
  statusFrom = ResourceLogisticsStatus.None,
  statusTo = ResourceLogisticsStatus.Done,
  currentStatus,
}: IsAllowedStatusArgs<ResourceLogisticsStatus>) => {
  const firstAvailableIndex = logisticsStatusList.indexOf(statusFrom);
  const lastAvailableIndex = logisticsStatusList.indexOf(statusTo);
  const currentIndex = logisticsStatusList.indexOf(currentStatus);
  return currentIndex >= firstAvailableIndex && currentIndex <= lastAvailableIndex;
};

export const isEmployeeOnCase = (employeeId: Uuid, caseData: Case) => {
  const employeeGroups = Object.values(caseData?.assignedEmployees ?? {});
  const isAssignedUser = employeeGroups.some((group) => group.some((e) => e.employeeId === employeeId));
  const isResource = caseData?.resources.some((r) => r.resourceId === employeeId);
  return Boolean(isAssignedUser || isResource);
};

export const getRouteProviders = (legs?: (AviaLegTypes | GroundLegTypes)[]): string[] => {
  if (!legs?.length) return [];

  const uniqRouteTypes = uniqBy(legs, 'legType').map((leg) => leg.legType);

  return uniqRouteTypes.map((type) => caseLogisticsProviders[routeLegToProviderTypeMap[type]]);
};

export const getOutcomeReasonForTransplantNotCompleted = (
  { outcomeType, turndownOutcome }: OutcomeData,
  delimiter: DataPresentation = DataPresentation.Colon,
): string => {
  if (
    turndownOutcome &&
    turndownOutcomeNames[turndownOutcome] &&
    (outcomeType === OutcomeType.DryRun || outcomeType === OutcomeType.Turndown)
  ) {
    return formatOutput(outcomeTypeNames[outcomeType], turndownOutcomeNames[turndownOutcome], delimiter);
  }

  return outcomeTypeNames[outcomeType] ?? '';
};

export const getGroupedReservationsOnCase = (reservations: ReservationData[]) => {
  const completedReservationStatuses = [ResourceStatus.Done, ResourceStatus.Canceled];
  const [completed, current] = partition(reservations, (r) => completedReservationStatuses.includes(r.status));
  return [completed, current];
};

const getBasicDetails = (record: ClinicalDataRecord): string | null => {
  return record.item?.details || record.items?.[0]?.details || null;
};

const extractMediaDetails = (record: ClinicalDataRecord): string | null => {
  if (record.items) {
    const mediaDetails = record.items
      .flatMap((item) => item.mediaFiles?.map((media) => media.details).filter(Boolean) || [])
      .join('\n');
    if (mediaDetails) return mediaDetails;
  }

  if (record.item?.mediaFiles) {
    const mediaDetails = record.item.mediaFiles
      .map((media) => media.details)
      .filter(Boolean)
      .join('\n');
    if (mediaDetails) return mediaDetails;
  }

  return null;
};

export const getDetailsFromClinicalRecord = (record: ClinicalDataRecord): string | null => {
  const details: string | null = getBasicDetails(record);
  if (details) return details;

  return extractMediaDetails(record);
};

export const isRandomizationScs = (randomization?: Randomization): boolean => {
  return randomization?.result === RandomizationResultType.SCS;
};

export const hasActiveTrialProgram = (organType: OrganType, trialPrograms?: TrialProgram[]): boolean => {
  const expectedProgramName = trialProgramByOrgan[organType];

  if (!expectedProgramName || !trialPrograms) {
    return false;
  }

  return trialPrograms.some(
    (program) => program.name === expectedProgramName && program.organ === organType && program.isActive,
  );
};

export const isHeartTypeA = (questionnaire?: TrialAnsweredQuestionModel[], organType?: OrganType): boolean => {
  const isEnhance = organType === OrganType.Heart;
  const isAnswerPartA = !!questionnaire?.some(
    (q) => q.question === TrialQuestion.TypeOfEnhanceTrial && q.answer === TrialAnswer.PartA,
  );
  return isEnhance && isAnswerPartA;
};

export const getRandomizationResultState = (
  randomization: Randomization | undefined,
  questionnaire: TrialAnsweredQuestionModel[] | undefined,
  organType: OrganType | undefined,
  hasOrganApproval: boolean,
) => {
  const isPartA = isHeartTypeA(questionnaire, organType);
  const hasRandomizationResult = !!randomization && !isPartA;
  const randomizationNotNeeded = isPartA && hasOrganApproval;
  return { hasRandomizationResult, randomizationNotNeeded };
};

export const isCaseTrial = (
  organType: OrganType,
  trialProgram?: TrialProgram,
  questionnaire?: TrialAnsweredQuestionModel[],
): boolean => {
  switch (organType) {
    case OrganType.Heart:
      return (
        (trialProgram?.isActive &&
          questionnaire?.some(
            (q) => q.question === TrialQuestion.RequestedRecoveryForEnhanceTrial && q.answer === TrialAnswer.Yes,
          )) ??
        false
      );
    case OrganType.Lungs:
      return (
        (trialProgram?.isActive &&
          questionnaire?.some(
            (q) => q.question === TrialQuestion.RequestedRecoveryForDenovoTrial && q.answer === TrialAnswer.Yes,
          )) ??
        false
      );
    default:
      return false;
  }
};

export const canRandomizationHappen = (
  organType: OrganType,
  trialProgram?: TrialProgram,
  questionnaire?: TrialAnsweredQuestionModel[],
): boolean => {
  const chosenPartA = isHeartTypeA(questionnaire, organType);
  switch (organType) {
    case OrganType.Heart:
      return isCaseTrial(organType, trialProgram, questionnaire) && !chosenPartA;
    case OrganType.Lungs:
      return isCaseTrial(organType, trialProgram, questionnaire);
    default:
      return false;
  }
};

export const isScsCustomerCase = ({
  organType,
  trialProgram,
  questionnaire,
  randomization,
}: {
  organType: OrganType;
  trialProgram?: TrialProgram;
  questionnaire?: TrialAnsweredQuestionModel[];
  randomization?: Randomization;
}): boolean => {
  return canRandomizationHappen(organType, trialProgram, questionnaire) && isRandomizationScs(randomization);
};

export const ocsToScs = (randomization?: Randomization, text?: string): string => {
  if (!text) return '';
  if (isRandomizationScs(randomization)) return text.replace('OCS', 'SCS');
  return text;
};

export const getCaseStatusName = (status: CaseStatus, randomizationType?: RandomizationResultType) => {
  switch (status) {
    case CaseStatus.OrganOnOCS:
      return randomizationType === RandomizationResultType.SCS ? 'On SCS' : caseStatusNames[status];
    case CaseStatus.OrganOffOCS:
      return randomizationType === RandomizationResultType.SCS ? 'SCS Handoff' : caseStatusNames[status];
    default:
      return caseStatusNames[status];
  }
};

export const shouldShowRandomizationResults = ({
  organType,
  trialProgram,
  questionnaire,
}: {
  organType: OrganType;
  trialProgram?: TrialProgram;
  questionnaire?: TrialAnsweredQuestionModel[];
}): boolean => {
  return isCaseTrial(organType, trialProgram, questionnaire);
};

const getTxCenterPathPrefix = (organizationId?: string): string => {
  return organizationId ? getUrl(['', 'organization', organizationId]) : '';
};

export const buildCaseUrl = ({
  organizationId,
  caseId,
  path,
}: {
  organizationId?: string;
  caseId: string;
  path?: string;
}): string => {
  const prefix = getTxCenterPathPrefix(organizationId);
  return getUrl([prefix, 'case', ...[caseId, path].filter(Boolean)] as string[]);
};

/** Ordered so `additionalOrgans` diffs cleanly and the fallback anchor is deterministic. */
export const CANONICAL_ORGAN_ORDER = Object.values(OrganType);

export const toOrganSet = (organTypes: (OrganType | null | undefined)[]): OrganType[] =>
  CANONICAL_ORGAN_ORDER.filter((organType) => organTypes.includes(organType));
